/**
 * Video Downloader — Download videos from URLs using yt-dlp
 * Stitch design system
 */
import React, { useState } from 'react';
import { Box, Typography, TextField, InputAdornment, LinearProgress, Tooltip } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';

/* ── Stitch tokens ─────────────────────────────────────────────────────────── */
const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  bg: '#f8f9fa', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const IconBadge = ({ name, size = 48, iconSize = 26, bg = T.primaryContainer, color = T.primary }) => (
  <Box sx={{
    width: size, height: size, borderRadius: '0.75rem', bgcolor: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }}>
    <Icon name={name} size={iconSize} sx={{ color }} />
  </Box>
);

const Badge = ({ label, color = T.primary, bg = T.primaryContainer }) => (
  <Typography component="span" sx={{
    fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
    color, bgcolor: bg, px: 1.25, py: 0.3, borderRadius: '9999px',
    display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.06em',
  }}>
    {label}
  </Typography>
);

/* ── Resolution options ────────────────────────────────────────────────────── */
const RESOLUTIONS = [
  { value: '2160', label: '4K', sub: '2160p', icon: 'hd' },
  { value: '1440', label: '2K', sub: '1440p', icon: 'hd' },
  { value: '1080', label: 'Full HD', sub: '1080p', icon: 'high_quality' },
  { value: '720', label: 'HD', sub: '720p', icon: 'sd' },
  { value: '480', label: 'SD', sub: '480p', icon: 'sd' },
];

const FORMATS = [
  { value: 'mp4', label: 'MP4', desc: 'Video met beeld en geluid', icon: 'videocam' },
  { value: 'mp3', label: 'MP3', desc: 'Alleen audio', icon: 'music_note' },
];

const PLATFORMS = [
  { name: 'YouTube', icon: 'play_circle' },
  { name: 'Vimeo', icon: 'movie' },
  { name: 'Twitter / X', icon: 'tag' },
  { name: 'Instagram', icon: 'photo_camera' },
  { name: 'TikTok', icon: 'music_note' },
  { name: 'Facebook', icon: 'group' },
];

/* ── Helpers ───────────────────────────────────────────────────────────────── */
const formatSize = (bytes) => {
  if (!bytes) return null;
  const mb = bytes / (1024 * 1024);
  return mb >= 1000 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function VideoDownloader() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [format, setFormat] = useState('mp4');
  const [resolution, setResolution] = useState('1080');

  /* ── Paste ─────────────────────────────────────────────────────────────── */
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text);
      setError(null);
      setVideoInfo(null);
    } catch { setError('Kon niet plakken van clipboard'); }
  };

  const handleClear = () => {
    setUrl('');
    setError(null);
    setSuccess(null);
    setVideoInfo(null);
  };

  /* ── Get info ──────────────────────────────────────────────────────────── */
  const handleGetInfo = async () => {
    if (!url) { setError('Voer een URL in'); return; }

    setLoading(true);
    setError(null);
    setVideoInfo(null);

    try {
      const res = await authFetch(`${BACKEND_URL}/Api/video-downloader/info?url=${encodeURIComponent(url)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kon video info niet ophalen');

      setVideoInfo(data);
      if (data.resolutions?.length > 0) {
        const currentAvailable = data.resolutions.includes(parseInt(resolution));
        if (!currentAvailable) setResolution(String(data.resolutions[0]));
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  /* ── Download ──────────────────────────────────────────────────────────── */
  const handleDownload = async () => {
    if (!url) { setError('Voer een URL in'); return; }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await authFetch(`${BACKEND_URL}/Api/video-downloader/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, format, resolution }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Download mislukt');
      }

      const disposition = res.headers.get('Content-Disposition');
      let filename = format === 'mp3' ? 'audio.mp3' : 'video.mp4';
      if (disposition) {
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;\s]+)/i);
        if (utf8Match) filename = decodeURIComponent(utf8Match[1]);
        else {
          const regularMatch = disposition.match(/filename="([^"]+)"/i);
          if (regularMatch) filename = decodeURIComponent(regularMatch[1]);
        }
      }

      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      setSuccess(filename);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ width: '100%', maxWidth: 860, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      <PageHeader
        icon="download"
        title="Video Downloader"
        subtitle="Download video's van Vimeo, X en 1000+ andere sites"
      />

      {/* YouTube warning */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        p: 2, mb: 3, borderRadius: '0.625rem',
        bgcolor: '#fef3c7', border: '1px solid #fde68a',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#d97706' }}>warning</span>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: '#92400e' }}>
          YouTube wordt momenteel niet ondersteund door deze tool.
        </Typography>
      </Box>

      {/* ── URL input card ──────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: T.surface, borderRadius: '1rem', p: 3, mb: 3, boxShadow: T.shadow }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
          Video URL
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth size="small"
            placeholder="https://www.youtube.com/watch?v=..."
            value={url}
            onChange={(e) => { setUrl(e.target.value); setError(null); setVideoInfo(null); }}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="link" size={18} sx={{ color: url ? T.primary : T.outlineVariant }} />
                </InputAdornment>
              ),
              endAdornment: url ? (
                <InputAdornment position="end">
                  <Box onClick={!loading ? handleClear : undefined} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
                    <Icon name="close" size={16} sx={{ color: T.outlineVariant }} />
                  </Box>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow,
                fontFamily: T.fontBody,
                '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
                '&.Mui-focused fieldset': { borderColor: T.primary },
              },
            }}
          />

          {/* Paste button */}
          <Tooltip title="Plakken van clipboard" arrow
            slotProps={{
              tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem', px: 1.5, py: 0.75 } },
              arrow: { sx: { color: T.text, '&::before': { border: 'none' } } },
            }}
          >
            <Box
              onClick={!loading ? handlePaste : undefined}
              sx={{
                width: 40, height: 40, borderRadius: '0.5rem', flexShrink: 0,
                bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.15s',
                '&:hover': { bgcolor: T.surfaceHigh },
              }}
            >
              <Icon name="content_paste" size={18} sx={{ color: T.outline }} />
            </Box>
          </Tooltip>
        </Box>

        {/* Check button */}
        <Box
          onClick={!loading && url ? handleGetInfo : undefined}
          sx={{
            py: 1.25, borderRadius: '0.5rem', cursor: !loading && url ? 'pointer' : 'default',
            background: !loading && url
              ? `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`
              : T.surfaceHigh,
            color: !loading && url ? '#fff' : T.outlineVariant,
            fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            boxShadow: !loading && url ? '0 4px 12px rgba(99,89,131,0.25)' : 'none',
            transition: 'all 0.2s',
            '&:hover': !loading && url ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)', transform: 'translateY(-1px)' } : {},
          }}
        >
          {loading && !videoInfo ? (
            <>
              <Box sx={{
                width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid #fff', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
              }} />
              Ophalen…
            </>
          ) : (
            <>
              <Icon name="search" size={18} sx={{ color: 'inherit' }} />
              Check Video
            </>
          )}
        </Box>
      </Box>

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          p: 2, mb: 3, borderRadius: '0.75rem',
          bgcolor: T.errorBg, border: `1px solid ${T.error}20`,
        }}>
          <Icon name="error" size={18} sx={{ color: T.error }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.error, flex: 1 }}>
            {error}
          </Typography>
          <Box onClick={() => setError(null)} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
            <Icon name="close" size={16} sx={{ color: T.error }} />
          </Box>
        </Box>
      )}

      {/* ── Success ─────────────────────────────────────────────────────── */}
      {success && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          p: 2, mb: 3, borderRadius: '0.75rem',
          bgcolor: T.emeraldBg, border: `1px solid ${T.emerald}20`,
        }}>
          <Icon name="check_circle" size={18} sx={{ color: T.emerald }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.emerald }}>
              Download gestart
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>
              {success}
            </Typography>
          </Box>
          <Box onClick={() => setSuccess(null)} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
            <Icon name="close" size={16} sx={{ color: T.emerald }} />
          </Box>
        </Box>
      )}

      {/* ── Video info + options ────────────────────────────────────────── */}
      {videoInfo && (
        <Box sx={{ bgcolor: T.surface, borderRadius: '1rem', overflow: 'hidden', boxShadow: T.shadow, mb: 3 }}>

          {/* Thumbnail + info header */}
          <Box sx={{ display: 'flex', gap: 0, flexDirection: { xs: 'column', sm: 'row' } }}>
            {videoInfo.thumbnail && (
              <Box sx={{
                width: { xs: '100%', sm: 260 }, minHeight: 146, flexShrink: 0,
                position: 'relative', overflow: 'hidden',
              }}>
                <Box
                  component="img" src={videoInfo.thumbnail} alt={videoInfo.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {/* Duration overlay */}
                {videoInfo.duration && (
                  <Box sx={{
                    position: 'absolute', bottom: 8, right: 8,
                    bgcolor: 'rgba(0,0,0,0.7)', borderRadius: '0.35rem',
                    px: 0.75, py: 0.25, display: 'flex', alignItems: 'center', gap: 0.5,
                  }}>
                    <Icon name="schedule" size={12} sx={{ color: '#fff' }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', fontWeight: 600, color: '#fff' }}>
                      {formatDuration(videoInfo.duration)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ p: 2.5, flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontFamily: T.font, fontSize: '1rem', fontWeight: 700, color: T.text,
                mb: 0.75, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {videoInfo.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {videoInfo.uploader && (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>
                    {videoInfo.uploader}
                  </Typography>
                )}
                {videoInfo.platform && (
                  <Badge label={videoInfo.platform} />
                )}
                {formatSize(videoInfo.filesize) && (
                  <Badge label={`~${formatSize(videoInfo.filesize)}`} color={T.outline} bg={T.surfaceLow} />
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ height: 1, bgcolor: T.surfaceLow }} />

          {/* Format + resolution + download */}
          <Box sx={{ p: 2.5 }}>

            {/* Format selector */}
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
              Formaat
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
              {FORMATS.map(f => (
                <Box
                  key={f.value}
                  onClick={() => setFormat(f.value)}
                  sx={{
                    flex: 1, py: 1.5, px: 2, borderRadius: '0.75rem', cursor: 'pointer',
                    bgcolor: format === f.value ? (f.value === 'mp3' ? T.tertiaryContainer : T.primaryContainer) : T.surfaceLow,
                    border: `1.5px solid ${format === f.value ? (f.value === 'mp3' ? `${T.tertiary}40` : `${T.primary}40`) : 'transparent'}`,
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: format === f.value ? undefined : T.surfaceHigh },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon name={f.icon} size={18} sx={{ color: format === f.value ? (f.value === 'mp3' ? T.tertiary : T.primary) : T.outlineVariant }} />
                    <Typography sx={{
                      fontFamily: T.font, fontSize: '0.88rem', fontWeight: 700,
                      color: format === f.value ? T.text : T.outline,
                    }}>
                      {f.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.secondary, mt: 0.25 }}>
                    {f.desc}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Resolution selector (MP4 only) */}
            {format === 'mp4' && (
              <>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                  Resolutie
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
                  {RESOLUTIONS.map(opt => {
                    const available = !videoInfo.resolutions?.length || videoInfo.resolutions.includes(parseInt(opt.value));
                    const selected = resolution === opt.value;
                    return (
                      <Box
                        key={opt.value}
                        onClick={available ? () => setResolution(opt.value) : undefined}
                        sx={{
                          px: 1.75, py: 1, borderRadius: '0.5rem',
                          cursor: available ? 'pointer' : 'default',
                          bgcolor: selected ? T.primaryContainer : available ? T.surfaceLow : 'transparent',
                          border: `1.5px solid ${selected ? `${T.primary}40` : available ? 'transparent' : `${T.outlineVariant}40`}`,
                          opacity: available ? 1 : 0.35,
                          transition: 'all 0.15s',
                          textAlign: 'center', minWidth: 64,
                          '&:hover': available && !selected ? { bgcolor: T.surfaceHigh } : {},
                          ...((!available) && { borderStyle: 'dashed' }),
                        }}
                      >
                        <Typography sx={{
                          fontFamily: T.font, fontSize: '0.78rem', fontWeight: 700,
                          color: selected ? T.primary : available ? T.text : T.outlineVariant,
                          textDecoration: available ? 'none' : 'line-through',
                        }}>
                          {opt.label}
                        </Typography>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', color: T.outline }}>
                          {opt.sub}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}

            {/* Download button */}
            <Box
              onClick={!loading ? handleDownload : undefined}
              sx={{
                py: 1.5, borderRadius: '0.75rem', cursor: !loading ? 'pointer' : 'default',
                background: format === 'mp3'
                  ? `linear-gradient(135deg, ${T.tertiary} 0%, #7c6ba0 100%)`
                  : `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
                color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.88rem',
                boxShadow: '0 4px 12px rgba(99,89,131,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                transition: 'all 0.2s',
                '&:hover': !loading ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)', transform: 'translateY(-1px)' } : {},
                ...(loading && { opacity: 0.8 }),
              }}
            >
              {loading ? (
                <>
                  <Box sx={{
                    width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid #fff', borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
                  }} />
                  Downloaden…
                </>
              ) : (
                <>
                  <Icon name="download" size={20} sx={{ color: '#fff' }} />
                  {`Download ${format.toUpperCase()}${format === 'mp4' ? ` (${resolution}p)` : ''}`}
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Supported platforms ─────────────────────────────────────────── */}
      <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, boxShadow: T.shadow }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
          Ondersteunde platforms
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {PLATFORMS.map(p => (
            <Box key={p.name} sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.25, py: 0.5, borderRadius: '9999px',
              bgcolor: T.surfaceLow, transition: 'all 0.15s',
            }}>
              <Icon name={p.icon} size={14} sx={{ color: T.primary }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.text }}>
                {p.name}
              </Typography>
            </Box>
          ))}
          <Box
            component="a"
            href="https://github.com/yt-dlp/yt-dlp/blob/master/supportedsites.md"
            target="_blank" rel="noopener"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.25, py: 0.5, borderRadius: '9999px',
              bgcolor: T.tertiaryContainer, textDecoration: 'none',
              transition: 'all 0.15s',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <Icon name="add" size={14} sx={{ color: T.tertiary }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.tertiary }}>
              1000+ meer
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
